import { BlobticDataManager } from './blobticDataManager';
import { getBlobticUrl } from '../config/api.config';

export interface RealtimeEntityEvent {
  type: 'EntityCreated' | 'EntityUpdated' | 'EntityDeleted' | string;
  objectName: string;
  data: any;
  dosIdf?: number;
  timestamp?: string;
}

type RealtimeListener = (event: RealtimeEntityEvent) => void;

class BlobticRealtimeService {
  private socket: WebSocket | null = null;
  private listeners: Set<RealtimeListener> = new Set();
  private reconnectTimer: any = null;
  private currentDosIdf: number = 1;
  private isConnected: boolean = false;

  public connect(dosIdf: number = 1) {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname.toLowerCase();
      if (path.endsWith('/login') || path.includes('/login') || path.startsWith('/student-pre-registration')) {
        return;
      }
    }
    this.currentDosIdf = dosIdf;
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }

    try {
      const httpUrl = getBlobticUrl('ZOOM');
      let wsUrl: string;

      if (httpUrl.startsWith('http://') || httpUrl.startsWith('https://')) {
        const urlObj = new URL(httpUrl);
        const protocol = urlObj.protocol === 'https:' ? 'wss:' : 'ws:';
        wsUrl = `${protocol}//${urlObj.host}/api/blobtic/ws?dosIdf=${dosIdf}`;
      } else {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        wsUrl = `${protocol}//${window.location.host}/api/blobtic/ws?dosIdf=${dosIdf}`;
      }

      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = () => {
        this.isConnected = true;
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
      };

      this.socket.onmessage = async (event: MessageEvent) => {
        try {
          const message: RealtimeEntityEvent = JSON.parse(event.data);
          if (message && message.objectName) {
            await this.handleIncomingMessage(message);
          }
        } catch (err) {
          console.error('BlobticRealtimeService: message parse error', err);
        }
      };

      this.socket.onclose = () => {
        this.isConnected = false;
        this.scheduleReconnect();
      };

      this.socket.onerror = () => {
        this.isConnected = false;
      };
    } catch (err) {
      console.error('BlobticRealtimeService: connect error', err);
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect(this.currentDosIdf);
    }, 5000);
  }

  public subscribe(listener: RealtimeListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private async handleIncomingMessage(event: RealtimeEntityEvent) {
    const { type, objectName, data } = event;
    if (!objectName || !data) return;

    const cleanObjectName = objectName.includes(',') 
      ? objectName.split(',').pop()!.trim().toLowerCase() 
      : objectName.trim().toLowerCase();

    const normalizedType = (type || '').toLowerCase();

    if (normalizedType === 'entitycreated' || normalizedType === 'entityupdated' || normalizedType === 'insert' || normalizedType === 'update') {
      await BlobticDataManager.upsertItem(cleanObjectName, data);
    } else if (normalizedType === 'entitydeleted' || normalizedType === 'delete') {
      await BlobticDataManager.removeItem(cleanObjectName, data);
    }

    this.listeners.forEach((listener) => {
      try {
        listener(event);
      } catch (err) {
        console.error('BlobticRealtimeService listener error', err);
      }
    });
  }

  public disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this.isConnected = false;
  }
}

export const blobticRealtimeService = new BlobticRealtimeService();
